import { HTMLAttributes } from 'react';
import { TextArea } from 'semantic-ui-react';
import {
  FormSchemaAwareWidgetProps,
  AutoSaveSchemaAwareWidget,
  AutoSaveRadioSelectGroup,
  RadioSelectGroupProps,
  NumberInputProps,
  AutoSaveNumberInput
} from '~/components/form';
import { useProjectContext } from '~/hooks';
import './styles.scss';

type WorksheetFieldWidths = 'full' | 'half' | 'third' | 'quarter';

const WidthDivisorMap = {
  full: 1,
  half: 2,
  third: 3,
  quarter: 4
};

interface WorksheetFieldTextInputProps extends FormSchemaAwareWidgetProps {
  type?: 'dropdown' | 'text' | 'textarea'
  width?: WorksheetFieldWidths
}

interface WorksheetFieldRadioInputProps extends RadioSelectGroupProps {
  type: 'radio'
  fieldLabel: string
}

interface WorksheetFieldNumberInputProps extends NumberInputProps {
  type: 'number'
  width?: WorksheetFieldWidths
}

type WorksheetFieldProps = WorksheetFieldTextInputProps | WorksheetFieldRadioInputProps | WorksheetFieldNumberInputProps;

export const WorksheetField = ({ type = 'text', ...props }: WorksheetFieldProps) => {

  const width = (props as WorksheetFieldTextInputProps | WorksheetFieldNumberInputProps)?.width;
  const maxFieldWidth = width && width !== 'full' ? `calc(42rem / ${WidthDivisorMap[width]} - (var(--medium) / 2))` : '100%';

  const {
    conditions: {
      isFieldDisplaying,
      getValidRadioValues,
      getFieldProps
    }
  } = useProjectContext();

  const displayStateProps = getFieldProps(props.schemaKey) ?? {};

  props = {
    ...props,
    ...displayStateProps
  };

  if (!isFieldDisplaying(props.schemaKey)) {
    return null;
  }

  const anchorId = `field-${props.schemaKey}`;

  if (type === 'number') {
    // Format number input props
    const numberInputProps = props as WorksheetFieldNumberInputProps;
    return (
      <div
        id={anchorId}
        style={{ maxWidth: maxFieldWidth ?? '100%', width: '100%', scrollMarginTop: '8rem' }}
        className="form-input"
      >
        <AutoSaveNumberInput {...numberInputProps} />
      </div>
    );
  } else if (type === 'radio') {
    const { values, ...radioProps } = props as WorksheetFieldRadioInputProps;
    return (
      <div id={anchorId} className="form-input" style={{ scrollMarginTop: '8rem' }}>
        <AutoSaveRadioSelectGroup values={getValidRadioValues(props.schemaKey, values ?? [])} {...radioProps}/>
      </div>
    );
  } else {
    // Format text input props
    const textInputProps = props as WorksheetFieldTextInputProps;

    // Set custom widget type if needed
    const Widget = type === 'textarea' ? TextArea : undefined;

    const isDropdown = type === 'dropdown';
    const className = `form-input${isDropdown ? ' form-input--dropdown' : ''}`;
    const wrapperStyle = isDropdown
      ? { maxWidth: maxFieldWidth ?? '100%', scrollMarginTop: '8rem' }
      : { maxWidth: maxFieldWidth ?? '100%', width: '100%', scrollMarginTop: '8rem' };

    return (
      <div id={anchorId} style={wrapperStyle} className={className}>
        <AutoSaveSchemaAwareWidget as={Widget} {...textInputProps} />
      </div>
    );
  }
};

type WorksheetFieldRowProps = HTMLAttributes<HTMLDivElement> & { isQuote?: boolean };

WorksheetField.Row = function Row({ isQuote, className, children, ...rest }: WorksheetFieldRowProps) {
  const classes = [
    'worksheet-field-row',
    isQuote && 'worksheet-field-row--quote',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={classes} {...rest}>
      {children}
    </div>
  );
};